/**
 * Ayuda — Ventas → Remitos
 */

/** @type {import('../help-index.js').HelpSidebarItem} */
export const ADM_SAL_REMITS_ITEM = {
  id: 'remits',
  screenId: 'adm-sal-remits',
  label: 'Remitos',
  description: 'Emití y consultá remitos de entrega a clientes.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro:
          'El remito es el comprobante que documenta la entrega de mercadería a un cliente. Registra qué productos salieron, en qué cantidad y para quién. Después podés facturar lo entregado o vincular el remito a una factura existente.',
      },
      {
        title: 'Paso 1 — Buscar remitos',
        intro: 'Arriba de la lista podés filtrar remitos ya emitidos:',
        fields: [
          {
            label: 'Fecha Desde / Fecha Hasta',
            description: 'Acotá la búsqueda por el período en que se emitieron los remitos.',
          },
          {
            label: 'Cliente',
            description: 'Escribí el nombre y elegilo de la lista para ver solo sus remitos.',
          },
          {
            label: 'Botón Buscar',
            description: 'Actualiza la lista con los filtros elegidos.',
          },
          {
            label: 'Filtro avanzado',
            description: 'Tocá «Filtro Avanzado >» para mostrar el campo Número y buscar un remito puntual por su numeración.',
          },
        ],
      },
      {
        title: 'Paso 2 — Leer la lista',
        intro: 'La lista muestra:',
        bullets: [
          'Fecha — día de emisión.',
          'Cliente — a quién se le entregó.',
          'Tipo Rem — tipo de remito configurado (por ejemplo Remito R).',
          'Número — punto de venta y número del comprobante (ej. 0001-00000123).',
          'Ícono de lápiz — por ahora no permite editar; el sistema avisa que la modificación no está disponible.',
          'Ícono de cruz (×) — eliminar el remito.',
        ],
        paragraphs: [
          'Tocá una fila (fuera del lápiz y la cruz) para ver el detalle completo del remito.',
        ],
      },
      {
        title: 'Paso 3 — Cargar un remito nuevo',
        intro:
          'El botón verde + abre la ventana «Remito a Clientes». Completá los datos y tocá «Confirmar». Si salió bien, el formulario se limpia para cargar otro.',
      },
      {
        title: 'Encabezado del remito nuevo',
        fields: [
          {
            label: 'Fecha',
            description: 'Fecha del remito. Es obligatoria.',
          },
          {
            label: 'Tipo de remito',
            description: 'Elegí el tipo de comprobante de remito que corresponda. Al elegirlo, aparecen los puntos de venta habilitados para ese tipo.',
          },
          {
            label: 'Punto de venta',
            description: 'Numeración del lugar desde donde emitís (según lo configurado en el sistema).',
          },
          {
            label: 'Número',
            description: 'Número del remito. Completalo según la numeración de tu punto de venta.',
          },
          {
            label: 'Cliente',
            description: 'Buscá y elegí el cliente al que le entregás la mercadería. Es obligatorio. Si el cliente tiene una lista de precios, puede seleccionarse sola.',
          },
          {
            label: 'Lista de precios',
            description:
              'Opcional. Ves las mismas listas que en una factura. Si elegís una, el precio de cada artículo se completa al cargarlo, convertido a la moneda del remito. Si no tenés permiso para modificar precios, el campo queda bloqueado y solo podés usar el de la lista. «Sin lista» deja los precios vacíos.',
          },
          {
            label: 'Moneda',
            description:
              'Aparece al elegir el tipo de remito. Las opciones son las habilitadas para ese comprobante en Configuración → Comprobantes, igual que en las facturas. Si cargás un precio, la moneda es obligatoria. El precio de la línea queda en esa moneda.',
          },
        ],
      },
      {
        title: 'Crear desde factura',
        intro:
          'El botón «Crear desde Factura» busca facturas del cliente que aún tienen productos pendientes de remitir. Elegís la factura y el sistema carga las líneas con las cantidades correspondientes. Es útil cuando ya facturaste y ahora registrás la entrega.',
      },
      {
        title: 'Líneas del remito (productos)',
        intro: 'Cada fila es un producto que entregás:',
        fields: [
          {
            label: 'Producto',
            description: 'Buscá el artículo por nombre o código. Es obligatorio en cada línea.',
          },
          {
            label: 'Cantidad',
            description: 'Cuántas unidades entregás. Es obligatoria y debe ser mayor a cero.',
          },
          {
            label: 'Precio',
            description:
              'Opcional. Si hay una lista, se completa solo en la moneda del remito. Si tenés permiso para modificar precios, podés cambiarlo o dejarlo vacío. Al facturar el remito, ese precio se convierte a la moneda de la factura.',
          },
          {
            label: 'Almacén',
            description:
              'Si el producto lleva stock, el sistema puede pedirte de qué depósito sale. Aparece cuando el artículo es stockeable y hay más de un almacén configurado.',
          },
          {
            label: 'Botón +',
            description: 'Agrega una descripción extra para esa línea (texto opcional).',
          },
          {
            label: 'Botón x',
            description: 'Quita esa línea del remito.',
          },
          {
            label: 'Agregar línea',
            description: 'Sumá otro producto al remito.',
          },
        ],
        paragraphs: [
          'Si la línea viene de una factura, puede mostrarse el comprobante de origen al lado de la cantidad.',
        ],
      },
      {
        title: 'Observaciones y botones finales',
        fields: [
          {
            label: 'Observaciones',
            description: 'Notas generales del remito (opcional).',
          },
          {
            label: 'Limpiar formulario',
            description: 'Borra lo cargado para empezar un remito nuevo sin cerrar la ventana.',
          },
          {
            label: 'Confirmar',
            description: 'Guarda el remito. El sistema valida fecha, tipo, cliente y que cada línea tenga producto y cantidad.',
          },
        ],
      },
      {
        title: 'Ver el detalle de un remito',
        intro: 'Al tocar una fila de la lista ves:',
        bullets: [
          'Tipo, número y fecha del remito.',
          'Datos del cliente.',
          'Lista de productos: código, descripción, cantidad entregada y cantidad «Sin Facturar» (en rojo si aún falta facturar algo de esa línea).',
          'Observaciones, si las hubo.',
          'Quién lo creó y quién lo modificó, si corresponde.',
        ],
        paragraphs: [
          '«Ver Comprobantes Relacionados» muestra las facturas vinculadas a ese remito.',
          '«Eliminar remito» abre la confirmación de baja (también disponible con la cruz en la lista).',
        ],
      },
      {
        title: 'Eliminar un remito',
        intro:
          'Al confirmar la eliminación, el remito se borra y los movimientos de stock asociados se revierten (el stock vuelve a actualizarse). Tocá «SI» para confirmar o «NO» para cancelar.',
      },
    ],
  },
}
